import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  CaptableCompany,
  captableCompanySchema,
} from "../../utils/stage3types";
import { HStack, Error } from "../../components/utils";
import {
  currencies,
  Input,
  Label,
  Required,
  TextArea,
} from "../../utils/InputField";
import { FormatNumberSpan } from "../../utils/currencyRoboto";
import { toTitleCaseLabel } from "../../utils/Tooltip";
import { Select } from "../../components/inputComponent";
import {
  useGetPreSignedURL,
  useUpdateCompanyDetails,
} from "../../queries/onboarding";

export const CompanyDetails: React.FC<{
  companyDetails: CaptableCompany;
  id: string;
  confidence: number;
  onClose: Function;
  selectedPas3: string;
}> = ({ companyDetails, id, onClose, confidence, selectedPas3 }) => {
  const [isEditing, setIsEditing] = useState(false);
  const data = companyDetails || {};

  const [isSignedUrl, setIsSignedUrl] = useState<string>();
  const { data: signedUrl, refetch } = useGetPreSignedURL(
    isSignedUrl || "",
    isSignedUrl !== undefined,
  );
  const formatDateForInput = (dateStr: string): string => {
    if (!dateStr) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    return "";
  };

  const sanitizedCompanyDetails = Object.fromEntries(
    Object.entries(companyDetails || {}).map(([key, value]) => [
      key,
      value === null ? "" : value,
    ]),
  ) as CaptableCompany;

  const updatedDetails = {
    ...sanitizedCompanyDetails,
    lastRoundDate: formatDateForInput(
      String(sanitizedCompanyDetails.lastRoundDate || ""),
    ),
    captableDate: formatDateForInput(
      String(sanitizedCompanyDetails.captableDate || ""),
    ),
  };

  useEffect(() => {
    if (!isSignedUrl) return;

    const fetchSignedUrl = async () => {
      try {
        const result = await refetch();
        if (result.data) {
          window.open(result.data, "_blank");
        }
      } catch (error) {
        toast("Error Downloading File", { type: "error", autoClose: 2000 });
      }
    };

    fetchSignedUrl();
  }, [isSignedUrl]);

  const { mutate: updateCompanyDetails } = useUpdateCompanyDetails();

  const initialValues: CaptableCompany = companyDetails
    ? updatedDetails
    : {
        cin: "",
        companyName: "",
        captableDate: "",
        companyCurrency: "",
        jurisdiction: "",
        lastRoundDate: "",
        lastRoundSize: 0,
        sharePricePerShare: 0,
        totalFDBShares: 0,
        valuation: 0,
        lastRoundName: "",
        remarks: "",
      };

  const formik = useFormik<CaptableCompany>({
    initialValues,
    validationSchema: captableCompanySchema,
    validateOnChange: true,
    validateOnBlur: false,
    enableReinitialize: true,
    onSubmit: (data: CaptableCompany) => {
      updateCompanyDetails(
        {
          companyData: data as CaptableCompany,
          processedDataId: id,
        },
        {
          onSuccess: () => {
            toast("Excel file regenerated successfully", {
              autoClose: 2000,
              type: "success",
            });
            setIsEditing(false);
          },
          onError: (error: any) => {
            toast(error?.response?.data?.errorMessage, {
              type: "error",
              autoClose: 2000,
            });
          },
        },
      );
    },
  });

  const exceedList = ["companyName", "cin", "remarks"];

  return (
    <>
      <div
        className={`rounded-lg ${isEditing ? "h-[570px] py-2" : "h-[450px] mt-6 py-6"} overflow-auto scrollbar-hidden border bg-card text-card-foreground shadow-sm w-full border-t-4 border-t-blue-900 px-6`}
      >
        <div className="pb-2">
          {!isEditing && (
            <>
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold">{data?.companyName}</h2>
                  <p className="text-sm text-gray-500 font-semibold">
                    {data?.cin && `CIN: ${data?.cin}`}
                  </p>
                </div>
                <div className="flex justify-end items-start gap-2">
                  {selectedPas3 && selectedPas3 !== "" && (
                    <div
                      className="relative group cursor-pointer w-fit"
                      onClick={() => setIsSignedUrl(selectedPas3)}
                    >
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg"
                        alt="File"
                        className="w-8 h-8 object-contain"
                      />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-200 text-black text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                        {selectedPas3?.split("/").pop() ?? "PDF File"}
                      </div>
                    </div>
                  )}
                  <div>
                    <button
                      className="px-4 py-1 text-sm text-white border flex justify-end bg-blue-800 hover:bg-blue-900 rounded-md"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit Details
                    </button>
                    <p className="text-sm text-gray-700 font-bold">
                      {confidence && `Confidence: ${confidence}%`}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {!isEditing && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4 overflow-auto py-1">
              {(Object.keys(data) as Array<keyof typeof data>).map(
                (key, index) => {
                  const value: (typeof data)[typeof key] = data[key];

                  return (
                    !exceedList.includes(key) && (
                      <div key={index}>
                        <p className="text-sm text-gray-400 font-semibold">
                          {toTitleCaseLabel(key)}
                        </p>
                        <p className="font-semibold text-base">
                          {value === "" || value === null
                            ? "-"
                            : typeof value === "number"
                              ? FormatNumberSpan({
                                  value,
                                  format: "en-IN",
                                  position:
                                    "top-[-25px] left-2/6 -translate-x-[-5%]",
                                })
                              : value}
                        </p>{" "}
                      </div>
                    )
                  );
                },
              )}
            </div>
            <p className="text-sm text-gray-500 font-semibold">
              {data?.remarks && (
                <>
                  <p className="text-sm text-gray-400 font-semibold">Remarks</p>
                  <ul className="list-disc pl-5 text-base font-semibold text-gray-800">
                    {(data?.remarks ?? "-").split(".").map((item, index) => {
                      const trimmed = item.trim();
                      return trimmed ? <li key={index}>{trimmed}.</li> : null;
                    })}
                  </ul>
                </>
              )}
            </p>
          </>
        )}

        {isEditing && (
          <>
            <form onSubmit={formik.handleSubmit} className="">
              <div className="h-[495px] overflow-auto px-1 scrollbar-hidden">
                <HStack className="gap-6 mb-2">
                  <div className="flex-1">
                    <Label className="text-sm font-normal">
                      Company Name <Required />
                    </Label>
                    <Input
                      type="text"
                      placeholder="Enter Company Name"
                      className="w-96"
                      {...formik.getFieldProps(`companyName`)}
                    />
                    {formik.errors?.companyName && (
                      <Error text={formik.errors?.companyName} />
                    )}
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-normal">
                      CIN <Required />
                    </Label>
                    <Input
                      type="text"
                      placeholder="Enter CIN"
                      className="w-96"
                      {...formik.getFieldProps(`cin`)}
                    />
                    {formik.errors?.cin && <Error text={formik.errors?.cin} />}
                  </div>
                </HStack>

                <HStack className="gap-6 mb-2">
                  <div className="flex-1">
                    <Label className="text-sm font-normal">
                      Valuation <Required />
                    </Label>
                    <Input
                      type="number"
                      placeholder="Enter Company Valuation"
                      className="w-96"
                      {...formik.getFieldProps(`valuation`)}
                    />
                    {formik.errors?.valuation && (
                      <Error text={formik.errors?.valuation} />
                    )}
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-normal">
                      Last Round Size <Required />
                    </Label>
                    <Input
                      type="number"
                      placeholder="Enter Last Round Size"
                      className="w-96"
                      {...formik.getFieldProps(`lastRoundSize`)}
                    />
                    {formik.errors?.lastRoundSize && (
                      <Error text={formik.errors?.lastRoundSize} />
                    )}
                  </div>
                </HStack>

                <HStack className="gap-6 mb-2">
                  <div className="flex-1">
                    <Label className="text-sm font-normal">
                      Total FDB Shares <Required />
                    </Label>
                    <Input
                      type="number"
                      placeholder="Enter Total FDB Shares"
                      className="w-96"
                      {...formik.getFieldProps(`totalFDBShares`)}
                    />
                    {formik.errors?.totalFDBShares && (
                      <Error text={formik.errors?.totalFDBShares} />
                    )}
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-normal">
                      Share Price Per Share <Required />
                    </Label>
                    <Input
                      type="number"
                      placeholder="Enter Share Price Per Share"
                      className="w-96"
                      {...formik.getFieldProps(`sharePricePerShare`)}
                    />
                    {formik.errors?.sharePricePerShare && (
                      <Error text={formik.errors?.sharePricePerShare} />
                    )}
                  </div>
                </HStack>

                <HStack className="gap-6 mb-2">
                  <div className="flex-1">
                    <Label className="text-sm font-normal">
                      Captable As Of
                    </Label>
                    <Input
                      type="date"
                      placeholder="Enter Captable Date"
                      className="w-96"
                      {...formik.getFieldProps(`captableDate`)}
                    />
                    {formik.errors?.captableDate && (
                      <Error text={formik.errors?.captableDate} />
                    )}
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-normal">
                      Last Round Date
                    </Label>
                    <Input
                      type="date"
                      placeholder="Enter Last Round Date"
                      className="w-96"
                      {...formik.getFieldProps(`lastRoundDate`)}
                    />
                    {formik.errors?.lastRoundDate && (
                      <Error text={formik.errors?.lastRoundDate} />
                    )}
                  </div>
                </HStack>
                <HStack className="gap-6 mb-2">
                  <div className="flex-1">
                    <Label className="text-sm font-normal">
                      Last Round Name
                    </Label>
                    <Input
                      type="text"
                      placeholder="Enter Last Round Name"
                      className="w-96"
                      {...formik.getFieldProps(`lastRoundName`)}
                    />
                    {formik.errors?.lastRoundName && (
                      <Error text={formik.errors?.lastRoundName} />
                    )}
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-normal">
                      Company Currency <Required />
                    </Label>
                    <Select
                      className="w-96 mt-2"
                      options={currencies}
                      {...formik.getFieldProps("companyCurrency")}
                    />
                    {formik.errors?.companyCurrency && (
                      <Error text={formik.errors?.companyCurrency} />
                    )}
                  </div>
                </HStack>

                <HStack className="gap-6 mb-2">
                  <div className="flex-1">
                    <Label className="text-sm font-normal">Jurisdiction</Label>
                    <Input
                      type="text"
                      placeholder="Enter jurisdiction"
                      className="w-96"
                      {...formik.getFieldProps(`jurisdiction`)}
                    />
                    {formik.errors?.jurisdiction && (
                      <Error text={formik.errors?.jurisdiction} />
                    )}
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-normal">Remarks</Label>
                    <TextArea
                      placeholder="Enter Remarks"
                      {...formik.getFieldProps(`remarks`)}
                    />
                    {/* <Input
                      type="textarea"
                      placeholder="Enter Remarks"
                      className="w-96"
                      {...formik.getFieldProps(`remarks`)}
                    /> */}
                    {formik.errors?.remarks && (
                      <Error text={formik.errors?.remarks} />
                    )}
                  </div>
                </HStack>
              </div>
              <div className="flex justify-end items-center gap-4 mt-4">
                <button
                  type="button"
                  className="px-4 py-1 text-sm text-blue-800 border border-blue-800 hover:bg-blue-100 rounded-md"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1 text-sm text-white border bg-blue-800 hover:bg-blue-900 rounded-md"
                >
                  Submit
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
};
